// Form-state helpers for the unified vendor distribution (self | round_robin).

const LEGACY_POOL_MODES = ['roundRobin', 'weighted', 'distributeAgents'];

/** Empty distribution state for a brand-new vendor. */
export function distributionDefaults() {
  return {
    distributionMode: 'self',
    roundRobinConfig: { agents: [], overflowStrategy: 'even_split' },
  };
}

const idOf = (x) => (x && x._id ? String(x._id) : String(x));

/**
 * Derive form state from a stored vendor. Vendors saved before the unified
 * model are mapped: legacy pool/weighted modes become a round-robin pool
 * seeded from `assignments` (dailyCap) or `agentPool`; everything else → self.
 * @param {object} v - Vendor document from the API.
 * @returns {{distributionMode: 'self'|'round_robin', roundRobinConfig: object}}
 */
export function hydrateDistribution(v) {
  const stored = v?.roundRobinConfig?.agents || [];
  if (v?.distributionMode === 'round_robin') {
    return {
      distributionMode: 'round_robin',
      roundRobinConfig: {
        agents: stored.map((a) => ({
          agentId: idOf(a.agentId),
          enabled: a.enabled !== false,
          weightCap: a.weightCap || 10,
          assignedCountToday: a.assignedCountToday || 0,
        })),
        overflowStrategy: v.roundRobinConfig?.overflowStrategy || 'even_split',
      },
    };
  }
  if (LEGACY_POOL_MODES.includes(v?.distribution?.mode)) {
    const fromAssignments = (v.assignments || []).map((a) => ({
      agentId: idOf(a.agent),
      enabled: a.active !== false,
      weightCap: a.dailyCap > 0 ? a.dailyCap : 10,
      assignedCountToday: a.leadsToday || 0,
    }));
    const agents = fromAssignments.length
      ? fromAssignments
      : (v.agentPool || []).map((a) => ({ agentId: idOf(a), enabled: true, weightCap: 10, assignedCountToday: 0 }));
    if (agents.length) {
      return { distributionMode: 'round_robin', roundRobinConfig: { agents, overflowStrategy: 'even_split' } };
    }
  }
  return distributionDefaults();
}

/**
 * Wire fields for save. Legacy `distribution.mode` is pinned to 'account' (the
 * new fields drive routing); `agentPool` mirrors the pool so the Meta webhook
 * path keeps working.
 * @param {object} form
 * @returns {{distributionMode: string, roundRobinConfig: object, agentPool: string[]}}
 */
export function buildDistributionFields(form) {
  const rr = form.roundRobinConfig || distributionDefaults().roundRobinConfig;
  const isRR = form.distributionMode === 'round_robin';
  return {
    distributionMode: isRR ? 'round_robin' : 'self',
    roundRobinConfig: {
      overflowStrategy: rr.overflowStrategy,
      agents: (rr.agents || []).map((a) => ({
        agentId: a.agentId,
        enabled: a.enabled !== false,
        weightCap: Math.max(1, parseInt(a.weightCap, 10) || 10),
      })),
    },
    agentPool: isRR ? (rr.agents || []).filter((a) => a.enabled !== false).map((a) => a.agentId) : [],
  };
}
